import { useState, useEffect } from 'react';
import { Loader2, Key, Calculator, RefreshCw, Sparkles } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { UserAutocomplete } from '@/components/ui/user-autocomplete';
import { useUpdatePartnerAccount, useRecalculatePrices } from '@/api/partner-accounts';
import { useToast } from '@/components/ui/use-toast';
import type { PartnerAccount, OwnerInfo } from '@/types/api';

interface PartnerAccountSettingsDialogProps {
  account: PartnerAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReconnectClick: () => void;
}

const SYNC_INTERVAL_OPTIONS = [
  { value: '5', label: '5 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '360', label: '6 hours' },
  { value: '720', label: '12 hours' },
  { value: '1440', label: '24 hours' },
];

export function PartnerAccountSettingsDialog({
  account,
  open,
  onOpenChange,
  onReconnectClick,
}: PartnerAccountSettingsDialogProps): JSX.Element {
  const { toast } = useToast();
  const updateAccount = useUpdatePartnerAccount();
  const recalculatePrices = useRecalculatePrices();

  // Form state
  const [name, setName] = useState(account.name);
  const [syncInterval, setSyncInterval] = useState(String(account.syncIntervalMinutes));
  const [defaultOwner, setDefaultOwner] = useState<OwnerInfo | null>(account.defaultOwner ?? null);
  const [error, setError] = useState<string | null>(null);

  // Pricing formula state
  const [pricingFormulaEnabled, setPricingFormulaEnabled] = useState(account.pricingFormulaEnabled ?? true);
  const [priceMultiplier, setPriceMultiplier] = useState(String(account.priceMultiplier ?? 5));
  const [scoutaroundPremium, setScoutaroundPremium] = useState(String((account.scoutaroundPremium ?? 0.15) * 100));

  // What the AI is shown when it writes a title and description
  const [aiImageCount, setAiImageCount] = useState(String(account.aiImageCount ?? 5));
  const [aiIncludeFacadeImage, setAiIncludeFacadeImage] = useState(account.aiIncludeFacadeImage ?? true);

  // Track if form has changes
  const hasPricingChanges =
    pricingFormulaEnabled !== (account.pricingFormulaEnabled ?? true) ||
    priceMultiplier !== String(account.priceMultiplier ?? 5) ||
    scoutaroundPremium !== String((account.scoutaroundPremium ?? 0.15) * 100);

  const hasAiChanges =
    aiImageCount !== String(account.aiImageCount ?? 5) ||
    aiIncludeFacadeImage !== (account.aiIncludeFacadeImage ?? true);

  const hasChanges =
    name !== account.name ||
    syncInterval !== String(account.syncIntervalMinutes) ||
    JSON.stringify(defaultOwner) !== JSON.stringify(account.defaultOwner ?? null) ||
    hasPricingChanges ||
    hasAiChanges;

  // Reset state when dialog opens or account changes
  useEffect(() => {
    if (open) {
      setName(account.name);
      setSyncInterval(String(account.syncIntervalMinutes));
      setDefaultOwner(account.defaultOwner ?? null);
      setError(null);
      // Reset pricing formula state
      setPricingFormulaEnabled(account.pricingFormulaEnabled ?? true);
      setPriceMultiplier(String(account.priceMultiplier ?? 5));
      setScoutaroundPremium(String((account.scoutaroundPremium ?? 0.15) * 100));
      setAiImageCount(String(account.aiImageCount ?? 5));
      setAiIncludeFacadeImage(account.aiIncludeFacadeImage ?? true);
    }
  }, [open, account]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Account name is required');
      return;
    }

    // Validate pricing formula inputs
    const multiplierNum = parseFloat(priceMultiplier);
    const premiumNum = parseFloat(scoutaroundPremium);

    if (pricingFormulaEnabled) {
      if (isNaN(multiplierNum) || multiplierNum < 1 || multiplierNum > 100) {
        setError('Price multiplier must be between 1 and 100');
        return;
      }
      if (isNaN(premiumNum) || premiumNum < 0 || premiumNum > 100) {
        setError('Platform premium must be between 0% and 100%');
        return;
      }
    }

    try {
      // Build update payload - only include changed fields
      const updateData: {
        name?: string;
        syncIntervalMinutes?: number;
        defaultOwnerEmail?: string | null;
        pricingFormulaEnabled?: boolean;
        aiImageCount?: number;
        aiIncludeFacadeImage?: boolean;
        priceMultiplier?: number;
        scoutaroundPremium?: number;
      } = {};

      if (name !== account.name) {
        updateData.name = name.trim();
      }

      if (syncInterval !== String(account.syncIntervalMinutes)) {
        updateData.syncIntervalMinutes = parseInt(syncInterval, 10);
      }

      // Handle owner changes
      const currentOwnerEmail = account.defaultOwner?.email ?? null;
      const newOwnerEmail = defaultOwner?.email ?? null;
      if (newOwnerEmail !== currentOwnerEmail) {
        updateData.defaultOwnerEmail = newOwnerEmail;
      }

      // Handle pricing formula changes
      if (pricingFormulaEnabled !== (account.pricingFormulaEnabled ?? true)) {
        updateData.pricingFormulaEnabled = pricingFormulaEnabled;
      }
      if (priceMultiplier !== String(account.priceMultiplier ?? 5)) {
        updateData.priceMultiplier = multiplierNum;
      }
      if (scoutaroundPremium !== String((account.scoutaroundPremium ?? 0.15) * 100)) {
        updateData.scoutaroundPremium = premiumNum / 100; // Convert percentage to decimal
      }

      if (aiImageCount !== String(account.aiImageCount ?? 5)) {
        updateData.aiImageCount = parseInt(aiImageCount, 10);
      }

      if (aiIncludeFacadeImage !== (account.aiIncludeFacadeImage ?? true)) {
        updateData.aiIncludeFacadeImage = aiIncludeFacadeImage;
      }

      if (Object.keys(updateData).length === 0) {
        onOpenChange(false);
        return;
      }

      await updateAccount.mutateAsync({
        id: account.id,
        data: updateData,
      });

      toast({
        title: 'Settings saved',
        description: 'Partner account settings have been updated.',
      });

      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    }
  };

  const handleRecalculatePrices = async () => {
    try {
      const result = await recalculatePrices.mutateAsync(account.id);
      toast({
        title: 'Prices recalculated',
        description: `Updated prices for ${String(result.locationsUpdated)} locations (${String(result.publishedLocationsUpdated)} published).`,
      });
    } catch (err) {
      toast({
        title: 'Recalculation failed',
        description: err instanceof Error ? err.message : 'Failed to recalculate prices',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    if (!updateAccount.isPending && !recalculatePrices.isPending) {
      onOpenChange(false);
    }
  };

  const handleReconnectClick = () => {
    onOpenChange(false);
    onReconnectClick();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
          <DialogDescription>
            Configure settings for {account.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Account Name */}
            <div className="space-y-2">
              <Label htmlFor="account-name">Account Name</Label>
              <Input
                id="account-name"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                disabled={updateAccount.isPending}
                data-testid="settings-name-input"
              />
            </div>

            {/* Sync Interval */}
            <div className="space-y-2">
              <Label htmlFor="sync-interval">Sync Interval</Label>
              <p className="text-xs text-muted-foreground">
                How often to sync locations from {account.partnerType?.name ?? 'partner'}
              </p>
              <Select
                value={syncInterval}
                onValueChange={setSyncInterval}
                disabled={updateAccount.isPending}
              >
                <SelectTrigger id="sync-interval" data-testid="settings-sync-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SYNC_INTERVAL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Default Owner */}
            <div className="space-y-2">
              <Label htmlFor="default-owner">Default Location Owner</Label>
              <p className="text-xs text-muted-foreground">
                Locations published from this account will be owned by this user
              </p>
              <UserAutocomplete
                value={defaultOwner}
                onChange={setDefaultOwner}
                placeholder="Search by name or email..."
                disabled={updateAccount.isPending}
                data-testid="settings-default-owner"
              />
            </div>

            <Separator className="my-4" />

            {/* What the AI is shown */}
            <div className="space-y-4">
              <div className="space-y-0.5">
                <Label htmlFor="ai-image-count" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI Content
                </Label>
                <p className="text-xs text-muted-foreground">
                  What the AI sees when it writes a title and description
                </p>
              </div>

              <div className="space-y-2 pl-3 border-l-2 border-muted ml-2">
                <Label htmlFor="ai-image-count">Photos sent to the AI</Label>
                <Input
                  id="ai-image-count"
                  type="number"
                  min={0}
                  max={10}
                  step={1}
                  value={aiImageCount}
                  onChange={(e) => { setAiImageCount(e.target.value); }}
                  disabled={updateAccount.isPending}
                  data-testid="settings-ai-image-count"
                />
                <p className="text-xs text-muted-foreground">
                  0 to 10. Zero writes from the partner&apos;s own words alone; each photo costs a
                  little more per generation.
                </p>
              </div>

              <div className="flex items-center justify-between pl-3 border-l-2 border-muted ml-2">
                <div className="space-y-0.5">
                  <Label htmlFor="ai-include-facade">Include the street-level facade</Label>
                  <p className="text-xs text-muted-foreground">
                    Sends it first, ahead of the partner&apos;s photos. Turn this off for partners
                    who already photograph the frontage.
                  </p>
                </div>
                <Switch
                  id="ai-include-facade"
                  checked={aiIncludeFacadeImage}
                  onCheckedChange={setAiIncludeFacadeImage}
                  disabled={updateAccount.isPending}
                  data-testid="settings-ai-include-facade"
                />
              </div>
            </div>

            {/* Pricing Formula Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="pricing-formula-enabled" className="flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    Auto-Calculate Prices
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Calculate ScoutAround prices from partner pricing during sync
                  </p>
                </div>
                <Switch
                  id="pricing-formula-enabled"
                  checked={pricingFormulaEnabled}
                  onCheckedChange={setPricingFormulaEnabled}
                  disabled={updateAccount.isPending}
                  data-testid="settings-pricing-formula-enabled"
                />
              </div>

              {pricingFormulaEnabled && (
                <div className="space-y-4 pl-1 border-l-2 border-muted ml-2">
                  {/* Price Multiplier */}
                  <div className="space-y-2 pl-3">
                    <Label htmlFor="price-multiplier">Price Multiplier</Label>
                    <p className="text-xs text-muted-foreground">
                      Multiply the partner nightly rate (for film productions, typically 5x)
                    </p>
                    <Input
                      id="price-multiplier"
                      type="number"
                      min="1"
                      max="100"
                      step="0.5"
                      value={priceMultiplier}
                      onChange={(e) => { setPriceMultiplier(e.target.value); }}
                      disabled={updateAccount.isPending}
                      data-testid="settings-price-multiplier"
                    />
                  </div>

                  {/* ScoutAround Premium */}
                  <div className="space-y-2 pl-3">
                    <Label htmlFor="scoutaround-premium">Platform Premium (%)</Label>
                    <p className="text-xs text-muted-foreground">
                      ScoutAround platform fee added to final price (e.g., 15 = 15%)
                    </p>
                    <Input
                      id="scoutaround-premium"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={scoutaroundPremium}
                      onChange={(e) => { setScoutaroundPremium(e.target.value); }}
                      disabled={updateAccount.isPending}
                      data-testid="settings-scoutaround-premium"
                    />
                  </div>

                  {/* Formula explanation */}
                  <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground ml-3">
                    <p className="font-medium mb-1">Price Formulas:</p>
                    <p>Day = ((base × multiplier) + cleaning) × (1 + premium)</p>
                    <p>Hour = day price ÷ 12</p>
                    <p>Week = ((base × multiplier × 7 × weeklyFactor) + cleaning) × (1 + premium)</p>
                    <p className="mt-1 italic">Note: Weekly factor comes from partner (e.g., Guesty weekly discount)</p>
                  </div>

                  {/* Recalculate button */}
                  <div className="pl-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { void handleRecalculatePrices(); }}
                      disabled={updateAccount.isPending || recalculatePrices.isPending}
                      className="w-full"
                      data-testid="settings-recalculate-prices-btn"
                    >
                      {recalculatePrices.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Recalculate All Prices
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Separator className="my-4" />

            {/* Credentials Section */}
            <div className="space-y-2">
              <Label>API Credentials</Label>
              <p className="text-xs text-muted-foreground">
                Update your {account.partnerType?.name ?? 'partner'} API credentials
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={handleReconnectClick}
                disabled={updateAccount.isPending}
                className="w-full"
                data-testid="settings-update-credentials-btn"
              >
                <Key className="mr-2 h-4 w-4" />
                Update Credentials
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateAccount.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateAccount.isPending || !hasChanges}
              data-testid="settings-save-btn"
            >
              {updateAccount.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
